import { format, isValid, parse, startOfMonth } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { FormikErrors } from 'formik';
import { getTrans } from 'modules/i18n';
import { getLocale } from 'modules/i18n/utils';
import { ExperienceEntry } from 'utils/cv-form-db';
import { DEFAULT_TIMEZONE } from 'utils/date';
import { isRequired } from '../../utils';

export const formatDateForDisplay = (isoString: string): string => {
  if (!isoString) return '';

  const utcDate = new Date(isoString);
  if (!isValid(utcDate)) return '';

  const berlinDate = utcToZonedTime(utcDate, DEFAULT_TIMEZONE);

  const locale = getLocale();
  return format(berlinDate, 'MMMM yyyy', { locale });
};

export const parseDisplayToISO = (displayDate: string): string => {
  if (!displayDate) return '';

  try {
    const locale = getLocale();
    const berlinDate = parse(displayDate, 'MMMM yyyy', new Date(), { locale });
    if (!isValid(berlinDate)) return '';

    const utcDate = zonedTimeToUtc(startOfMonth(berlinDate), DEFAULT_TIMEZONE);
    return utcDate.toISOString();
  } catch {
    return '';
  }
};

export const isoToDisplay = (isoString: string): string => {
  if (!isoString) return '';

  const date = new Date(isoString);
  if (!isValid(date)) return '';

  return format(date, 'yyyy MMMM');
};

const generateAllDateOptions = () => {
  const nowUtc = new Date();
  const nowBerlin = utcToZonedTime(nowUtc, DEFAULT_TIMEZONE);
  const currentYear = nowBerlin.getFullYear();
  const currentMonth = nowBerlin.getMonth();
  const locale = getLocale();

  const options = [];

  for (let year = 1950; year <= currentYear; year++) {
    const monthEnd = year === currentYear ? currentMonth : 11;

    for (let month = 0; month <= monthEnd; month++) {
      const berlinMidnight = new Date(year, month, 1, 0, 0, 0, 0);
      const utcDate = zonedTimeToUtc(berlinMidnight, DEFAULT_TIMEZONE);

      options.push({
        value: utcDate.toISOString(),
        label: format(berlinMidnight, 'MMMM yyyy', { locale }),
        year,
        month
      });
    }
  }

  return options.reverse();
};

export const generateDateOptions = (startDate?: string) => {
  const allDateOptions = generateAllDateOptions();
  const startDateOptions = allDateOptions;

  let endDateOptions = allDateOptions;

  if (startDate?.includes('T')) {
    const startDateBerlin = utcToZonedTime(
      new Date(startDate),
      DEFAULT_TIMEZONE
    );
    const startYear = startDateBerlin.getFullYear();
    const startMonth = startDateBerlin.getMonth();

    endDateOptions = allDateOptions.filter(
      option =>
        option.year > startYear ||
        (option.year === startYear && option.month >= startMonth)
    );
  }

  return {
    startDateOptions,
    endDateOptions
  };
};

export const validateExperience = (experience: ExperienceEntry) => {
  const errors: FormikErrors<ExperienceEntry> = {};

  const fieldsToValidate = [
    {
      field: 'companyName',
      message: getTrans(
        'cvBuilderPage.professionalExperience.form.company.required.label'
      )
    },
    {
      field: 'position',
      message: getTrans(
        'cvBuilderPage.professionalExperience.form.position.required.label'
      )
    },
    {
      field: 'startDate',
      message: getTrans(
        'cvBuilderPage.professionalExperience.form.startDate.required.label'
      )
    },
    {
      field: 'description',
      message: getTrans(
        'cvBuilderPage.professionalExperience.form.jobDescription.required.label'
      )
    }
  ] as const;

  fieldsToValidate.forEach(({ field, message }) => {
    if (isRequired(experience[field as keyof ExperienceEntry] as string)) {
      errors[field] = message;
    }
  });

  if (!experience.currentlyWorking) {
    if (isRequired(experience.endDate as string)) {
      errors.endDate = getTrans(
        'cvBuilderPage.professionalExperience.form.endDate.required.label'
      );
    }
  }

  return errors;
};

export const isExperienceComplete = (values: ExperienceEntry) => {
  const errors = validateExperience(values);
  return Object.keys(errors).length === 0;
};
